const FAQ_ITEMS = [
  {
    question:
      'What is the difference between a Gedolim painting, a drawing, and a Gedolim picture or photo?',
    answer: [
      'A Gedolim picture or photo is usually a reproduction of an archival photograph — a printed or framed image of the gadol. A Gedolim drawing is an original artwork in charcoal or pencil, created by an artist using reference imagery. A Gedolim painting is done in oils, acrylic, or watercolor — also an artist-made work, not a photo reproduction. At Joodaic, all pieces are artist-made portraits, not photographic prints. The distinction matters because the finish, texture, and presence on the wall are entirely different.',
    ],
  },
  {
    question: 'What formats are available for Gedolim portraits?',
    answer: [
      'Gedolim portraits at Joodaic are available in several formats: charcoal and pencil drawings, oil and acrylic paintings, watercolor pieces, and mixed-media works that combine print with hand-finished elements. Modern format options include wood, metal, and glass. Most pieces are available as fine art prints from the original, framed or unframed, as well as original one-of-a-kind works.',
    ],
  },
  {
    question: 'Which Gedolim and Rebbes are available?',
    answer: [
      'The collection spans Litvish gedolim, Chassidic Rebbes, and Sephardic rabbanim. Portraits may include Rav Chaim Kanievsky, the Lubavitcher Rebbe, the Chofetz Chaim, Rav Ovadia Yosef, Reb Shayele of Kerestir, the Chazon Ish, and others, depending on current artist inventory. Browse the collection to see what is available. If you are looking for a specific figure not currently listed, you can commission a portrait through the <a href="/artist/custom_co_studio">commissions hub</a>.',
    ],
  },
  {
    question: 'What is the difference between an original painting and a print?',
    answer: [
      'An original painting is a one-of-a-kind work created directly by the artist — there is only one. A print is a high-quality reproduction of an original, available in multiple sizes at a more accessible price. Mixed-media pieces sit between the two: they begin as a print but receive hand-finished details from the artist, such as texture, paint, or gold leaf, that make the piece feel elevated and three-dimensional. Most homes use prints or mixed-media for everyday spaces; originals are usually chosen when someone wants a more central or lasting piece.',
    ],
  },
  {
    question: 'Are Gedolim portraits suitable as gifts?',
    answer: [
      'Yes. Gedolim portraits are among the most personal Jewish gifts available — particularly for bar mitzvahs, yahrzeits, and milestone occasions. A portrait of someone\'s own Rebbe or rav carries meaning that a general gift does not. For a bar mitzvah, a portrait connected to the boy\'s family tradition or yeshiva tends to feel more personal. For a yahrzeit, a portrait of the gadol being commemorated can be very meaningful. Mixed-media and framed originals work especially well as formal gifts.',
    ],
  },
  {
    question: 'Can I commission a portrait of a specific Rebbe or Gadol?',
    answer: [
      'Yes. If the figure you are looking for is not currently available in the collection, you can message an artist directly through their profile or submit a request through the <a href="/artist/custom_co_studio">commissions hub</a>, where you can specify the subject, size, style, and overall direction of the piece. Charcoal drawings and paintings are both available as commissioned work.',
    ],
  },
];

const getSchema = rootURL => {
  const gedolimURL = `${rootURL}/s/art/gedolim`;
  return [
    {
      '@type': 'CollectionPage',
      '@id': `${gedolimURL}`,
      name: 'Gedolim Portraits & Paintings',
      description:
        'Gedolim portraits and paintings by independent Jewish artists — charcoal drawings, oil and acrylic paintings, and mixed-media pieces of Rebbes, rabbanim, and tzadikim.',
      url: gedolimURL,
      isPartOf: { '@id': `${rootURL}/#website` },
      publisher: { '@id': `${rootURL}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${gedolimURL}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${rootURL}`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Art',
          item: `${rootURL}/s/art`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Gedolim & Rebbe Portraits',
          item: gedolimURL,
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id': `${gedolimURL}#faq`,
      mainEntity: FAQ_ITEMS.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer[0].replace(/<[^>]*>/g, ''),
        },
      })),
    },
  ];
};

const gedolimSeoConfig = {
  key: 'gedolim',
  match: ({ listingType, activeCollection }) =>
    listingType === 'art' && activeCollection === 'gedolim',
  accentColor: '--colorArt',
  links: [],
  title: 'Gedolim Portraits & Paintings | Rebbe & Rabbi Art | Joodaic',
  description:
    'Shop Gedolim portraits and paintings by independent Jewish artists — charcoal drawings, original paintings, and mixed-media pieces of Rebbes, rabbanim, and tzadikim, available as prints or originals.',
  ogImage: {
    url: 'https://sharetribe.imgix.net/65ce73ce-b04c-482e-9891-763611bdbd97/677ffd71-bb89-4f87-a7a4-21fe872574b7?auto=format&fit=clip&h=1024&w=1024&s=27643d9262bd7c1ba95baffb4105dba1',
    width: '1024',
    height: '1024',
  },
  getSchema,
  intro: {
    heading: 'Gedolim & Rebbe Portraits',
    tagline:
      'Gedolim portraits and paintings by independent Jewish artists — charcoal drawings, oil and acrylic paintings, and mixed-media pieces of Rebbes, rabbanim, and tzadikim, available as prints, originals, or hand-finished pieces.',
    collapsedHeading: 'Gedolim & Rebbe Portraits',
    bodyParagraphs: [
      'A Gedolim portrait is usually a personal choice: the rav someone learned from, the Rebbe a family follows, a gadol whose sefer shaped the home. The collection may include portraits of Rav Chaim Kanievsky, the Lubavitcher Rebbe, the Chofetz Chaim, Rav Ovadia Yosef, Reb Shayele of Kerestir, and other gedolim and Rebbes, depending on current artist inventory.',
    ],
  },
  bottomSections: [
    {
      id: 'positioning',
      type: 'prose',
      heading: 'Made for the wall',
      paragraphs: [
        "These portraits are not about documentation — they're about presence. Each piece is an artistic interpretation, created as wall art for the home. Some are quiet and traditional. Others bring more texture, depth, or a more modern style. What they share is that they're made to live in a space, not just be looked at.",
        'Prices range across the collection, from fine art prints to original paintings and hand-finished mixed-media pieces.',
        'Gedolim portraits are a common choice for bar mitzvahs, yahrzeits, and milestone gifts — particularly when the piece connects to the recipient\'s own rav or Rebbe.',
      ],
      paragraphsAfter: [
        'You can also explore <a href="/s/art/the_classics">Jerusalem &amp; Kotel art</a> if you\'re looking for place-based pieces.',
      ],
    },
    {
      id: 'drawings',
      title: 'Charcoal & Pencil Drawings',
      paragraphs: [
        'Charcoal and pencil are the classic medium for gedolim portraiture. The most iconic images of the Chofetz Chaim, Rav Moshe Feinstein, and other gedolim are drawn rather than painted — there is something in the texture of the line that suits the subject. Drawings in this collection are original works by artists, not reproductions of archival photographs. Most are available as fine art prints from the original drawing, framed or unframed.',
      ],
    },
    {
      id: 'paintings',
      title: 'Gedolim Paintings',
      paragraphs: [
        'Paintings bring a different quality — brushwork, colour, and surface that changes how a piece feels on the wall. This includes oil, acrylic, and watercolor pieces, ranging from traditional representational portraits to more expressive or loosely painted interpretations. Original paintings are one-of-a-kind. Fine art prints from original paintings are also available in multiple sizes.',
      ],
    },
    {
      id: 'mixed_media',
      title: 'Mixed Media & Modern Formats',
      paragraphs: [
        'Mixed-media pieces typically begin as a print and receive hand-finished elements from the artist — texture, paint, gold leaf, or other additions that make the piece feel three-dimensional and alive. Modern format pieces include wood engravings, metal, and glass — materials that give a portrait a different presence on the wall. These formats suit contemporary interiors and make strong gifts.',
      ],
    },
  ],
  faqHeading: 'FAQ',
  faq: FAQ_ITEMS,
};

export default gedolimSeoConfig;
